import AddBoxOutlined from "@mui/icons-material/AddBoxOutlined";
import Person from "@mui/icons-material/Person";
import Settings from "@mui/icons-material/Settings";
import type { SvgIconComponent } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import React, { memo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTopBarStore } from "../store/useTopBarStore";
import { useFilterContext } from "../contexts/FilterProvider";
import { MenuButtonType } from "../utils/models";
import { formatCustomTabName } from "./CustomDashboards/common";

const MenuButton = styled(Button)({
  color: "#13294B",
  fontWeight: 700,
  justifyContent: "center",
  // Make sure the icon stays colorized when we flip to variant="contained"
  "& .MuiSvgIcon-root path": {
    color: "#13294B",
  },
});

const MenuItem = ({ link, label, startIcon, sx, onClick }: MenuButtonType & { onClick?: () => void }) => {
  const renderIcon = () => {
    if (React.isValidElement(startIcon)) {
      return startIcon;
    } else {
      const Icon = startIcon as SvgIconComponent;
      return <Icon />;
    }
  };

  // Check if the current path starts with the link
  const isActive = location.pathname.startsWith(link) && label !== "Primary Market Research";

  return (
    <Link to={link} onClick={onClick}>
      <MenuButton variant={isActive ? "contained" : "text"} startIcon={startIcon ? renderIcon() : undefined} sx={sx}>
        {label}
      </MenuButton>
    </Link>
  );
};

function TopTabsMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { menus, isMedOrCom, customTabs, isLoading } = useTopBarStore();
  const { resetFilters } = useFilterContext();

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: "1em" }}>
      {menus.map((item, i) => {
        // Clicking "Chat" should reset filters just like starting a New chat does.
        // Reset the query too so the saved-search dropdown shows "All Data", not "Custom".
        const onClick = item.link === "/deepchat" ? () => resetFilters(true) : undefined;
        return <MenuItem key={i} {...item} onClick={onClick} />;
      })}

      {isMedOrCom &&
        customTabs.map((item, i) => (
          <MenuItem
            key={i}
            link={`/dashboards/custom/${i}`}
            label={formatCustomTabName(item.name)}
            startIcon={
              location.pathname.startsWith(`/dashboards/custom/${i}`) ? (
                <IconButton
                  component="div"
                  onClick={(e) => {
                    navigate(`/dashboards/custom/${i}/edit`);
                    e.preventDefault();
                  }}
                  sx={{ margin: "-0.5rem" }}
                >
                  <Settings fontSize="small" />
                </IconButton>
              ) : (
                Person
              )
            }
          />
        ))}

      {isMedOrCom && (
        <Box display="flex" sx={{ placeItems: "center" }}>
          {isLoading && <CircularProgress size="small" sx={{ gridArea: "1/1", width: "1.25rem", height: "1.25rem" }} />}
          {customTabs.length < 3 && (
            <Link
              to="/dashboards/custom/new"
              state={{ prevTab: location.pathname }}
              style={{ pointerEvents: isLoading ? "none" : undefined, gridArea: "1/1" }}
            >
              <MenuButton
                sx={{ height: "100%", minWidth: "0" }}
                variant={location.pathname === "/dashboards/custom/new" ? "contained" : "text"}
              >
                <AddBoxOutlined sx={{ gridArea: "1/1", opacity: isLoading ? 0.2 : 1 }} />
              </MenuButton>
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
}

export default memo(TopTabsMenu);
